'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Monitor,
  Timer,
  RotateCcw,
  Settings,
  Check,
  ChevronLeft,
  BookOpen,
  Ban,
  CircleDashed,
} from 'lucide-react';
import {
  SAMPLE_PASSAGE,
  SAMPLE_TOPIC,
  SAMPLE_DIFFICULTY,
  type ExamPreset,
} from '@/data/examPresets';
import type { ExamConfig } from './GovtTypingTestClient';
import { scoreExam, splitWords, type ExamResult, type WordState } from '../lib/scoring';

interface ExamInterfaceProps {
  exam: ExamPreset;
  config: ExamConfig;
  onReconfigure: () => void;
  onSubmit: (result: ExamResult) => void;
  onExit: () => void;
}

const SOURCE_WORDS = SAMPLE_PASSAGE.split(' ');
const TOTAL_KEYSTROKES = SAMPLE_PASSAGE.length;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Live class for already-completed passage words (blind mode hides errors). */
function completedWordClass(index: number, states: WordState[], blindMode: boolean): string {
  if (blindMode) return 'word-pending';
  const state = states[index];
  if (state === 'correct' || state === 'half') return 'word-correct';
  if (state === 'full') return 'word-incorrect';
  return 'word-pending';
}

function StatBox({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center px-3 py-1"
      style={{ borderLeft: '1px solid var(--border)', minWidth: 58 }}
    >
      <span
        className="font-mono-typing text-sm leading-none"
        style={{ color: accent || 'var(--foreground)' }}
      >
        {value}
      </span>
      <span className="type-metric-label">{label}</span>
    </div>
  );
}

export default function ExamInterface({
  exam,
  config,
  onReconfigure,
  onSubmit,
  onExit,
}: ExamInterfaceProps) {
  const { blindMode, backspaceLock, wordHighlight, autoScroll } = config.toggles;
  const durationSec = config.duration * 60;

  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [remainingSec, setRemainingSec] = useState(durationSec);

  const keystrokesRef = useRef(0);
  const [keystrokes, setKeystrokes] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const correctedRef = useRef<Set<number>>(new Set());
  const prevLenRef = useRef(0);
  const finishedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const currentWordRef = useRef<HTMLSpanElement>(null);

  const typedWords = useMemo(() => splitWords(typed), [typed]);
  // Word being typed = number of completed words (space-terminated words already counted).
  const currentIndex =
    typed.endsWith(' ') || typed.length === 0
      ? typedWords.length
      : Math.max(0, typedWords.length - 1);
  // In-progress characters of the current word (text after the last space).
  const currentInput =
    typed.endsWith(' ') || typed.length === 0 ? '' : typed.slice(typed.lastIndexOf(' ') + 1);

  // Render the current word character-by-character with a live caret.
  //  - Word Highlight ON  → untyped chars use foreground (word stands out).
  //  - Word Highlight OFF → untyped chars stay muted like the rest.
  //  - Blind Mode OFF     → typed chars go green (correct) / red (wrong).
  //  - Blind Mode ON      → no green/red revealed while typing.
  // Overflow characters are never injected into the passage (no garbage append).
  const renderCurrentWord = (word: string) => {
    const chars = word.split('');
    const typedLen = currentInput.length;
    const caretPos = Math.min(typedLen, chars.length);
    const untypedCls = wordHighlight ? 'text-foreground' : 'word-pending';
    return (
      <>
        {chars.map((char, i) => {
          let cls = untypedCls;
          if (i < typedLen && !blindMode) {
            cls = currentInput[i] === char ? 'text-correct' : 'text-incorrect';
          }
          return (
            <React.Fragment key={`cur-${i}`}>
              {i === caretPos && <span className="caret" aria-hidden="true" />}
              <span className={cls}>{char}</span>
            </React.Fragment>
          );
        })}
        {caretPos >= chars.length && <span className="caret" aria-hidden="true" />}
      </>
    );
  };

  // Live scoring over completed words only.
  const live = useMemo(() => {
    const completedCount = typed.endsWith(' ')
      ? typedWords.length
      : Math.max(0, typedWords.length - 1);
    let full = 0;
    let half = 0;
    const states: WordState[] = [];
    for (let i = 0; i < SOURCE_WORDS.length; i += 1) {
      if (i < completedCount) {
        if (typedWords[i] === SOURCE_WORDS[i]) {
          if (correctedRef.current.has(i)) {
            half += 1;
            states.push('half');
          } else {
            states.push('correct');
          }
        } else {
          full += 1;
          states.push('full');
        }
      } else {
        states.push('omission');
      }
    }
    const elapsedSec = started ? durationSec - remainingSec : 0;
    const minutes = Math.max(elapsedSec, 1) / 60;
    const grossWords = keystrokes / 5;
    const netWpm = Math.max(0, Math.round((grossWords - (full + half * 0.5)) / minutes));
    return { full, half, netWpm, states };
  }, [typed, typedWords, keystrokes, remainingSec, started, durationSec]);

  const handleSubmit = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinished(true);
    const elapsedSec =
      started && startTimeRef.current
        ? Math.min(durationSec, Math.round((Date.now() - startTimeRef.current) / 1000))
        : 0;
    const result = scoreExam({
      sourceWords: SOURCE_WORDS,
      typed,
      keystrokes: keystrokesRef.current,
      elapsedSec: elapsedSec || (started ? durationSec - remainingSec : 0),
      correctedIndices: correctedRef.current,
    });
    onSubmit(result);
  }, [typed, started, durationSec, remainingSec, onSubmit]);

  // Countdown timer.
  useEffect(() => {
    if (!started || finished) return;
    const id = setInterval(() => {
      setRemainingSec((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [started, finished]);

  // Auto-submit when time runs out.
  useEffect(() => {
    if (started && remainingSec === 0 && !finishedRef.current) {
      handleSubmit();
    }
  }, [remainingSec, started, handleSubmit]);

  // Keep the current word in view.
  useEffect(() => {
    if (autoScroll && currentWordRef.current) {
      currentWordRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentIndex, autoScroll]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (finished) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (backspaceLock) {
        e.preventDefault();
        return;
      }
      // Mark the word currently being edited as corrected → counts as a half mistake.
      correctedRef.current.add(currentIndex);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (finished) return;
    const value = e.target.value;
    // Backspace Lock backstop: reject any change that shortens the text
    // (covers cut, select-and-replace, Cmd/Ctrl+Backspace, etc.).
    if (backspaceLock && value.length < typed.length) {
      e.target.value = typed;
      return;
    }
    if (!started && value.length > 0) {
      setStarted(true);
      startTimeRef.current = Date.now();
    }
    // Keystrokes = characters inserted (robust to paste / IME / automation).
    const delta = value.length - prevLenRef.current;
    if (delta > 0) {
      keystrokesRef.current += delta;
      setKeystrokes(keystrokesRef.current);
    }
    prevLenRef.current = value.length;
    setTyped(value);
  };

  const handleReset = () => {
    finishedRef.current = false;
    correctedRef.current = new Set();
    keystrokesRef.current = 0;
    startTimeRef.current = null;
    prevLenRef.current = 0;
    setKeystrokes(0);
    setTyped('');
    setStarted(false);
    setFinished(false);
    setRemainingSec(durationSec);
    textareaRef.current?.focus();
  };

  const grossWordsLive = keystrokes / 5;
  const elapsedMinLive = Math.max(started ? durationSec - remainingSec : 0, 1) / 60;
  const liveWpm = started ? Math.max(0, Math.round(grossWordsLive / elapsedMinLive)) : null;

  const isLowTime = remainingSec <= 30 && started;
  const timerColor = isLowTime ? 'var(--incorrect)' : 'var(--foreground)';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb + title band */}
      <div className="mb-5">
        <button
          type="button"
          onClick={onExit}
          className="btn-ghost inline-flex items-center gap-1.5 text-sm px-2 py-1 mb-3 -ml-2"
        >
          <ChevronLeft size={15} /> All exams
        </button>
        <div
          className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 pb-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2 className="text-xl md:text-2xl text-foreground">{exam.name}</h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-faint">
            <span>
              Target <span className="font-mono text-foreground">{exam.targetWpm}</span> wpm
            </span>
            <span>{config.duration} minutes</span>
            <span>{exam.organisation}</span>
          </div>
        </div>
      </div>

      {/* Exam sheet */}
      <div className="card-elevated overflow-hidden">
        {/* Toolbar */}
        <div
          className="flex flex-wrap items-stretch justify-between gap-3 pl-4 pr-2 py-2"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Monitor size={15} className="text-faint" />
            <span className="hidden sm:inline">{exam.name}</span>
            <span className="sm:hidden">{exam.shortName}</span>
          </div>
          <div className="flex items-stretch gap-2">
            <div className="flex items-center" style={{ border: '1px solid var(--border)' }}>
              <div
                className="flex items-center gap-1.5 px-3 font-mono-typing text-base self-stretch"
                style={{
                  color: timerColor,
                  background: isLowTime ? 'var(--incorrect-soft)' : 'transparent',
                }}
              >
                <Timer size={14} />
                {pad(Math.floor(remainingSec / 60))}:{pad(remainingSec % 60)}
              </div>
              <StatBox label="WPM" value={liveWpm === null ? '—' : String(liveWpm)} />
              <StatBox label="Keys" value={String(keystrokes)} />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleReset}
                className="restart-icon-btn flex items-center justify-center"
                aria-label="Reset test"
              >
                <RotateCcw size={15} />
              </button>
              <button
                type="button"
                onClick={onReconfigure}
                className="restart-icon-btn flex items-center justify-center"
                aria-label="Configure test"
              >
                <Settings size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* Passage meta bar */}
        <div
          className="flex flex-wrap items-center justify-between gap-2 px-4 py-2"
          style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-faint">
            <span className="eyebrow">{SAMPLE_TOPIC}</span>
            <span>{SAMPLE_DIFFICULTY}</span>
            <span>
              {TOTAL_KEYSTROKES.toLocaleString()} keystrokes · {SOURCE_WORDS.length} words
            </span>
          </div>
          <span className="text-xs text-muted-foreground font-mono">
            {started
              ? `Word ${Math.min(currentIndex + 1, SOURCE_WORDS.length)} / ${SOURCE_WORDS.length}`
              : 'Click below and start typing…'}
          </span>
        </div>

        {/* Passage display */}
        <div
          className="px-5 py-5 max-h-52 overflow-y-auto typing-area-scroll"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <p
            className="font-mono-typing text-[1.05rem] leading-[2]"
            style={{ textAlign: 'justify' }}
          >
            {SOURCE_WORDS.map((word, i) => {
              const isCurrent = i === currentIndex;
              return (
                <span
                  key={`${word}-${i}`}
                  ref={isCurrent ? currentWordRef : undefined}
                  className={
                    isCurrent
                      ? 'relative'
                      : i > currentIndex
                        ? 'word-pending'
                        : completedWordClass(i, live.states, blindMode)
                  }
                >
                  {isCurrent ? renderCurrentWord(word) : word}{' '}
                </span>
              );
            })}
          </p>
          {blindMode && (
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-faint">
              <Ban size={12} /> Blind mode on — mistakes are hidden until you finish.
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="px-5 py-5">
          <textarea
            ref={textareaRef}
            value={typed}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            disabled={finished}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            placeholder="Click here and start typing…"
            className="w-full min-h-[130px] resize-none p-4 font-mono-typing text-base leading-relaxed focus:outline-none focus:border-foreground transition-colors"
            style={{
              backgroundColor: 'var(--background)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              color: 'var(--foreground)',
            }}
          />
        </div>

        {/* Footer stats */}
        <div
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5"
          style={{ borderTop: '1px solid var(--border)', background: 'var(--muted)' }}
        >
          <div className="flex items-center gap-5 text-xs">
            <span className="flex items-center gap-1.5 text-faint">
              <Ban size={12} /> Full{' '}
              <b className="font-mono-typing font-medium" style={{ color: 'var(--incorrect)' }}>
                {blindMode ? '—' : live.full}
              </b>
            </span>
            <span className="flex items-center gap-1.5 text-faint">
              <CircleDashed size={12} /> Half{' '}
              <b className="font-mono-typing font-medium" style={{ color: 'var(--warning)' }}>
                {blindMode ? '—' : live.half}
              </b>
            </span>
            <span className="flex items-center gap-1.5 text-faint">
              Net WPM{' '}
              <b className="font-mono-typing font-medium text-foreground">
                {blindMode ? '—' : started ? live.netWpm : '—'}
              </b>
            </span>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="btn-ghost inline-flex items-center gap-1.5 text-xs px-2 py-1"
          >
            <BookOpen size={12} /> New passage
          </button>
        </div>
      </div>

      {/* Submit */}
      <div className="mt-5 flex justify-center">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!started}
          className="btn-primary inline-flex items-center gap-2 px-8 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check size={16} />
          Submit test
        </button>
      </div>
    </div>
  );
}
